/**
 * `/log` — the mobile quick log.
 *
 * "The moment meeting notes are most accurate is a rep in a car park, on a
 * phone, ten minutes after the meeting." One mobile-first flow and nothing
 * else: account → opportunity → **at least one customer attendee** → notes →
 * next step → action items → save.
 *
 * **It creates a `meetings` row**, not a note. `last_customer_contact_at` is
 * computed from meetings that have a customer attendee (§5.8), so a note-only
 * quick log would leave the deal flagged "no contact in 30 days" ten minutes
 * after the rep actually spoke to the customer — which is the single most
 * visible way a stall signal loses credibility.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useApiQuery, type Paged, type Row } from '@/lib/queries';
import { useToast } from '@/lib/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { AccountPicker } from '@/features/pickers/AccountPicker';
import { addDays, today } from '@/lib/format';
import { cn } from '@/lib/utils';

export function QuickLog(): React.ReactElement {
  const navigate = useNavigate();
  const { push } = useToast();

  const [accountId, setAccountId] = useState<string | null>(null);
  const [opportunityId, setOpportunityId] = useState<string>('');
  const [attendees, setAttendees] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [nextStep, setNextStep] = useState('');
  const [nextStepDue, setNextStepDue] = useState(addDays(today(), 10));
  const [actions, setActions] = useState<string[]>([]);
  const [draftAction, setDraftAction] = useState('');
  const [busy, setBusy] = useState(false);

  const opportunities = useApiQuery<Paged<Row>>(
    ['opportunities', 'quicklog', accountId ?? ''],
    accountId === null
      ? null
      : // `exact`, as in MeetingDialog: the account's own deals (session 6).
        `/api/opportunities?accountId=${accountId}&accountScope=exact&pageSize=50&sort=-updated_at`,
  );
  const contacts = useApiQuery<Paged<Row>>(
    ['contacts', 'quicklog', accountId ?? ''],
    accountId === null ? null : `/api/contacts?accountId=${accountId}&pageSize=100`,
  );

  const save = async (): Promise<void> => {
    if (accountId === null || attendees.length === 0) return;
    setBusy(true);
    try {
      const meeting = await api<{ meeting: { id: string } }>('/api/meetings', {
        method: 'POST',
        body: {
          subject: 'Quick log',
          meeting_type: 'call',
          starts_at: new Date().toISOString(),
          account_id: accountId,
          opportunity_id: opportunityId === '' ? null : opportunityId,
          notes: notes.trim() === '' ? null : notes.trim(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      });

      for (const contactId of attendees) {
        await api('/api/meeting-attendees', {
          method: 'POST',
          body: {
            meeting_id: meeting.meeting.id,
            attendee_type: 'customer',
            contact_id: contactId,
          },
        });
      }

      if (opportunityId !== '' && nextStep.trim() !== '') {
        await api(`/api/opportunities/${opportunityId}`, {
          method: 'PUT',
          body: { next_step: nextStep.trim(), next_step_due_date: nextStepDue },
        });
      }

      for (const title of actions) {
        await api('/api/action-items', {
          method: 'POST',
          body: {
            title,
            due_date: nextStepDue,
            account_id: accountId,
            meeting_id: meeting.meeting.id,
            owner_type: 'internal',
            ...(opportunityId === '' ? {} : { opportunity_id: opportunityId }),
          },
        });
      }

      push({ tone: 'success', title: 'Logged' });
      navigate(opportunityId === '' ? `/accounts/${accountId}` : `/opportunities/${opportunityId}`);
    } catch (error) {
      push({
        tone: 'error',
        title: error instanceof ApiError ? error.message : 'Nothing was saved.',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-md space-y-4 pb-24">
      <header>
        <h1 className="text-lg font-semibold">Quick log</h1>
        <p className="text-xs text-muted-foreground">
          Ten seconds now beats a perfect note that never gets written.
        </p>
      </header>

      <Field label="Account">
        <AccountPicker value={accountId} onChange={setAccountId} allowCreate={false} />
      </Field>

      {accountId !== null && (
        <Field label="Opportunity" hint="Most recently touched first. Optional.">
          <div className="space-y-1">
            {(opportunities.data?.items ?? []).slice(0, 6).map((opportunity) => {
              const id = String(opportunity['id']);
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setOpportunityId(opportunityId === id ? '' : id)}
                  className={cn(
                    'flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm',
                    opportunityId === id ? 'border-primary bg-accent' : 'border-input',
                  )}
                >
                  <span className="min-w-0 truncate">{String(opportunity['name'])}</span>
                  {opportunityId === id && <Check className="h-4 w-4" aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        </Field>
      )}

      {accountId !== null && (
        <Field
          label="Who did you speak to?"
          hint="At least one. A meeting with no customer attendee does not clear the no-contact signal."
        >
          <div className="flex flex-wrap gap-1.5">
            {(contacts.data?.items ?? []).map((contact) => {
              const id = String(contact['id']);
              const selected = attendees.includes(id);
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() =>
                    setAttendees((current) =>
                      selected ? current.filter((existing) => existing !== id) : [...current, id],
                    )
                  }
                  className={cn(
                    'rounded-full border px-3 py-1.5 text-sm',
                    selected ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
                  )}
                >
                  {String(contact['first_name'])} {String(contact['last_name'])}
                </button>
              );
            })}
            {(contacts.data?.items ?? []).length === 0 && (
              <p className="text-xs text-muted-foreground">
                No contacts on this account yet — add one from the account page first.
              </p>
            )}
          </div>
        </Field>
      )}

      <Field label="What happened?">
        <Textarea rows={5} value={notes} onChange={(event) => setNotes(event.target.value)} />
      </Field>

      <Field label="Next step">
        <Input value={nextStep} onChange={(event) => setNextStep(event.target.value)} />
      </Field>
      <Field label="By when">
        <Input
          type="date"
          value={nextStepDue}
          onChange={(event) => setNextStepDue(event.target.value)}
        />
      </Field>

      <Field label="Anything to chase?">
        <div className="flex gap-2">
          <Input
            value={draftAction}
            placeholder="Add an action"
            onChange={(event) => setDraftAction(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && draftAction.trim() !== '') {
                setActions((current) => [...current, draftAction.trim()]);
                setDraftAction('');
              }
            }}
          />
          <Button
            variant="outline"
            onClick={() => {
              if (draftAction.trim() === '') return;
              setActions((current) => [...current, draftAction.trim()]);
              setDraftAction('');
            }}
          >
            Add
          </Button>
        </div>
        {actions.length > 0 && (
          <ul className="mt-2 flex flex-wrap gap-1">
            {actions.map((action, index) => (
              <li key={index}>
                <Badge variant="outline">{action}</Badge>
              </li>
            ))}
          </ul>
        )}
      </Field>

      <div className="fixed inset-x-0 bottom-0 border-t bg-card p-3">
        <Button
          className="w-full"
          onClick={() => void save()}
          disabled={busy || accountId === null || attendees.length === 0}
        >
          {busy ? 'Saving…' : 'Save'}
        </Button>
        {attendees.length === 0 && (
          <p className="mt-1 text-center text-[11px] text-muted-foreground">
            Pick at least one person you spoke to.
          </p>
        )}
      </div>
    </div>
  );
}
